import React, { useEffect, useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { trackGoogleAdsConversion, trackBooking, trackFreeConsultation } from '../utils/gtag';

// Declare Calendly type for TypeScript
declare global {
  interface Window {
    Calendly: {
      initInlineWidget: (options: {
        url: string;
        parentElement: HTMLElement;
        prefill?: Record<string, string>;
        utm?: Record<string, string>;
      }) => void;
    };
  }
}

const BookingForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const initializeCalendly = () => {
      const calendlyElement = document.getElementById('calendly-inline-widget');
      if (calendlyElement && window.Calendly) {
        try {
          window.Calendly.initInlineWidget({
            url: 'https://calendly.com/sharon-sharonclare-hypnotherapy/discovery-call',
            parentElement: calendlyElement,
            prefill: {},
            utm: {}
          });
          setIsLoading(false);
        } catch (error) {
          console.error('Error initializing Calendly widget:', error);
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    // Listen for Calendly events
    // Function to determine conversion value based on event type
    const getConversionValue = (eventName: string): number => {
      const name = eventName.toLowerCase();

      // Discovery Call & Initial Consultation - £50
      if (name.includes('discovery call') || name.includes('initial consultation')) {
        return 50;
      }

      // Therapy Packs
      if (name.includes('4 session therapy pack')) {
        return 260;
      }
      if (name.includes('6 session therapy pack')) {
        return 380;
      }

      // All other sessions - £200
      // (Radiotherapy, Stop Smoking, Hypnotherapy Session, Relaxation)
      return 200;
    };

    const handleCalendlyEvent = (e: MessageEvent) => {
      if (e.origin === 'https://calendly.com' && e.data.event) {
        // When a meeting is scheduled
        if (e.data.event === 'calendly.event_scheduled') {
          console.log('Calendly booking completed:', e.data);

          // Extract user data from Calendly payload for enhanced conversions
          const payload = e.data.payload;
          let userData: any = undefined;

          // Get event name to determine conversion value
          const eventName = payload?.event?.name || 'Unknown';
          const conversionValue = getConversionValue(eventName);

          if (payload && payload.invitee) {
            // Parse name if available
            const name = payload.invitee.name || '';
            const nameParts = name.split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            userData = {
              email: payload.invitee.email || undefined,
              phone: payload.invitee.phone_number || undefined,
              firstName: firstName || undefined,
              lastName: lastName || undefined,
              address: {
                country: 'GB' // Most bookings will be from UK
              }
            };

            // Remove undefined values
            Object.keys(userData).forEach(key => {
              if (userData[key] === undefined) delete userData[key];
            });

            if (userData.address && Object.keys(userData.address).length === 0) {
              delete userData.address;
            }
          }

          // Track booking in GA4
          trackBooking('calendly_widget', 'inline');

          // Fire Google Ads conversion with dynamic value based on service type
          trackGoogleAdsConversion(conversionValue, 'GBP', userData);

          console.log(`✅ Google Ads conversion tracked: ${eventName} - £${conversionValue}`);
        }
      }
    };

    // Add event listener for Calendly events
    window.addEventListener('message', handleCalendlyEvent);

    // Check if Calendly is already loaded
    if (window.Calendly) {
      initializeCalendly();
    } else {
      // Load Calendly script if not already loaded
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.onload = () => {
        initializeCalendly();
      };
      script.onerror = () => {
        setHasError(true);
        setIsLoading(false);
      };
      document.head.appendChild(script);
    }

    // Cleanup event listener
    return () => {
      window.removeEventListener('message', handleCalendlyEvent);
    };
  }, []);

  return (
    <div className="bg-white rounded-2xl p-8 shadow-xl">
      <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-6">
        Book Your Free Consultation
      </h2>
      
      {/* Trust badges */}
      <div className="grid md:grid-cols-2 gap-4 mb-8" role="list" aria-label="Consultation benefits">
        <div className="flex items-center space-x-3 text-gray-700" role="listitem">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" aria-hidden="true" />
          <span>10 minutes completely free</span>
        </div>
        <div className="flex items-center space-x-3 text-gray-700" role="listitem">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" aria-hidden="true" />
          <span>No obligation discussion</span>
        </div>
        <div className="flex items-center space-x-3 text-gray-700" role="listitem">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" aria-hidden="true" />
          <span>Personalized treatment plan</span>
        </div>
        <div className="flex items-center space-x-3 text-gray-700" role="listitem">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" aria-hidden="true" />
          <span>Online or in-person options</span>
        </div>
      </div>

      {/* Calendly Widget Container */}
      <div className="calendly-container">
        {isLoading && (
          <div className="text-center py-8" role="status" aria-live="polite">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600 mx-auto mb-4" aria-hidden="true"></div>
            <p className="text-gray-600">Loading booking calendar...</p>
          </div>
        )}
        
        {hasError && (
          <div className="text-center py-8" role="alert" aria-live="assertive">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <p className="text-red-600 mb-4 font-semibold">Unable to load booking calendar</p>
              <p className="text-sm text-gray-600 mb-4">
                Please try the direct booking link or contact us directly.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="https://calendly.com/sharon-sharonclare-hypnotherapy/discovery-call"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackFreeConsultation()}
                  className="bg-accent-600 text-white px-6 py-3 rounded-lg text-center hover:bg-accent-700 transition-colors inline-flex items-center justify-center"
                  aria-label="Book consultation on Calendly"
                >
                  Book on Calendly
                </a>
                <a
                  href="tel:07932139245"
                  className="bg-green-600 text-white px-6 py-3 rounded-lg text-center hover:bg-green-700 transition-colors inline-flex items-center justify-center"
                  aria-label="Call Sharon Clare at 07932 139245"
                >
                  Call 07932 139245
                </a>
                <a
                  href="mailto:Sharon@sharonclare-hypnotherapy.com"
                  className="bg-gray-600 text-white px-6 py-3 rounded-lg text-center hover:bg-gray-700 transition-colors inline-flex items-center justify-center"
                  aria-label="Email Sharon Clare"
                >
                  Email Sharon
                </a>
              </div>
            </div>
          </div>
        )}
        
        <div 
          id="calendly-inline-widget"
          className="calendly-inline-widget"
          style={{ 
            minWidth: '320px', 
            height: '700px',
            width: '100%',
            maxWidth: '900px',
            margin: '0 auto',
            display: isLoading || hasError ? 'none' : 'block'
          }}
        />
      </div>

      <style jsx>{`
        .calendly-container {
          position: relative;
          width: 100%;
          max-width: 900px;
          margin: 0 auto;
        }
        
        .calendly-inline-widget {
          border-radius: 12px;
          overflow: hidden;
        }
        
        @media (max-width: 768px) {
          .calendly-inline-widget {
            min-width: 280px;
            height: 600px;
          }
        }
      `}</style>
    </div>
  );
};

export default BookingForm;
